/**
 * Diálogo de Importación masiva de Residentes desde Excel.
 *
 * Flujo:
 *  1) Subir Excel (opcionalmente descargar la plantilla) → Preview
 *  2) Confirmar y ejecutar
 *
 * Anti-duplicado por DNI. Auto-crea obras sociales que no existan.
 */
import { useRef, useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Upload, FileSpreadsheet, Loader2, AlertTriangle, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export function ImportResidentsDialog({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(1); // 1 = upload, 2 = preview, 3 = result
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const reset = () => {
    setStep(1); setFile(null); setPreview(null); setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };
  const onClose = (o) => { if (!o) { reset(); onOpenChange(false); } else onOpenChange(true); };

  const downloadTemplate = async () => {
    try {
      const r = await axios.get(`${BACKEND_URL}/api/residents/import/template`, {
        withCredentials: true, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_residentes.xlsx';
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Error al descargar plantilla');
    }
  };

  const runPreview = async () => {
    if (!file) { toast.error('Elegí un archivo Excel'); return; }
    setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${BACKEND_URL}/api/residents/import/preview`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(r.data);
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al analizar el archivo');
    } finally { setLoading(false); }
  };

  const runExecute = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${BACKEND_URL}/api/residents/import/execute`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(r.data);
      setStep(3);
      toast.success(`${r.data.created_count} residentes importados`);
      if (onSuccess) onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al importar');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="import-residents-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
            Importar residentes desde Excel {step === 1 ? '· Seleccionar archivo' : step === 2 ? '· Revisar' : '· Resultado'}
          </DialogTitle>
          <DialogDescription>
            Subí un Excel con las columnas: Nombres, Apellidos, Sexo, Fecha Nacimiento, Documento, Teléfono, Obra Social Nombre, Obra Social N° Afiliado.
          </DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4">
            <div className="rounded-md border border-blue-200 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800 p-3 text-xs text-blue-800 dark:text-blue-200 space-y-1">
              <p className="font-medium">¿Cómo funciona?</p>
              <ul className="list-disc pl-5 space-y-0.5">
                <li>Los residentes con un DNI que ya existe en el sistema se <b>saltean</b> (no se duplican).</li>
                <li>Las obras sociales que no estén en el catálogo se <b>crean automáticamente</b> en modalidad consolidada.</li>
                <li>Los datos fiscales se autocompletan con el DNI como tax_id e IVA &ldquo;Consumidor final&rdquo; (los podés editar después).</li>
                <li>Habitación, fecha de ingreso, responsables y demás campos quedan en blanco para completar después.</li>
              </ul>
            </div>

            <div className="flex justify-between items-center">
              <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn">
                <FileSpreadsheet className="w-4 h-4 mr-2" />Descargar plantilla vacía
              </Button>
            </div>

            <div>
              <label className="block">
                <span className="block text-sm font-medium mb-1">Archivo Excel</span>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".xlsx,.xlsm"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm border rounded-md p-2 file:bg-emerald-600 file:text-white file:border-0 file:rounded file:px-3 file:py-1 file:mr-3 file:cursor-pointer hover:file:bg-emerald-700"
                  data-testid="import-file-input"
                />
              </label>
              {file && <p className="text-xs text-muted-foreground mt-1">Seleccionado: <span className="font-mono">{file.name}</span> ({Math.round(file.size / 1024)} KB)</p>}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onClose(false)}>Cancelar</Button>
              <Button onClick={runPreview} disabled={!file || loading} data-testid="import-preview-btn">
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Analizar archivo
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 2 && preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <Card><CardContent className="pt-4">
                <div className="text-[11px] text-muted-foreground">Total filas</div>
                <div className="text-2xl font-bold">{preview.rows_total}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-[11px] text-muted-foreground">A importar</div>
                <div className="text-2xl font-bold text-emerald-700" data-testid="import-ok-count">{preview.to_create}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-[11px] text-muted-foreground">DNI ya existe</div>
                <div className="text-2xl font-bold text-amber-700" data-testid="import-dup-count">{preview.duplicates}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4">
                <div className="text-[11px] text-muted-foreground">Errores</div>
                <div className="text-2xl font-bold text-red-700" data-testid="import-error-count">{preview.errors}</div>
              </CardContent></Card>
            </div>

            {preview.new_insurers_to_create?.length > 0 && (
              <div className="rounded-md border border-violet-200 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-800 p-3 text-xs text-violet-800 dark:text-violet-200">
                <p className="font-medium mb-1">Se crearán {preview.new_insurers_to_create.length} obras sociales nuevas:</p>
                <p>{preview.new_insurers_to_create.join(' · ')}</p>
              </div>
            )}

            <div className="max-h-[400px] overflow-y-auto border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-16">Fila</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead>Nombre</TableHead>
                    <TableHead>DNI</TableHead>
                    <TableHead>OS</TableHead>
                    <TableHead>N° Afiliado</TableHead>
                    <TableHead>Nac.</TableHead>
                    <TableHead>Motivo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.items.map((it) => (
                    <TableRow key={it.row_number} className={
                      it.status === 'error' ? 'bg-red-50/50' :
                      it.status === 'duplicate' ? 'bg-amber-50/50' : ''
                    } data-testid={`import-row-${it.row_number}`}>
                      <TableCell className="font-mono text-xs">{it.row_number}</TableCell>
                      <TableCell>
                        {it.status === 'ok' && <Badge className="bg-emerald-100 text-emerald-800">OK</Badge>}
                        {it.status === 'duplicate' && <Badge className="bg-amber-100 text-amber-800">Duplicado</Badge>}
                        {it.status === 'error' && <Badge className="bg-red-100 text-red-800">Error</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{it.full_name || `${it.first_name || ''} ${it.last_name || ''}`}</TableCell>
                      <TableCell className="font-mono text-xs">{it.dni || '—'}</TableCell>
                      <TableCell className="text-xs">{it.insurer_name || '—'}</TableCell>
                      <TableCell className="font-mono text-xs">{it.insurance_member_number || '—'}</TableCell>
                      <TableCell className="text-xs">{it.birth_date || '—'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{it.reason || ''}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={() => { setStep(1); setPreview(null); }} data-testid="import-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" />Volver
              </Button>
              <Button
                onClick={runExecute}
                disabled={loading || preview.to_create === 0}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="import-execute-btn"
              >
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Upload className="w-4 h-4 mr-2" />
                Importar {preview.to_create} residente{preview.to_create === 1 ? '' : 's'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && result && (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800 p-4 flex items-start gap-3" data-testid="import-result-banner">
              <CheckCircle2 className="w-6 h-6 text-emerald-600 flex-shrink-0" />
              <div className="space-y-1">
                <p className="font-medium text-emerald-800 dark:text-emerald-200">Importación exitosa</p>
                <p className="text-sm text-emerald-700 dark:text-emerald-300">
                  Se crearon <b>{result.created_count}</b> residentes
                  {result.duplicates_count > 0 && <> · {result.duplicates_count} duplicados omitidos</>}
                  {result.errors_count > 0 && <> · {result.errors_count} con errores</>}.
                </p>
                {result.new_insurers_created?.length > 0 && (
                  <p className="text-xs text-emerald-700 dark:text-emerald-300">
                    Obras sociales creadas: {result.new_insurers_created.join(', ')}
                  </p>
                )}
              </div>
            </div>

            {result.errors?.length > 0 && (
              <details className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/30 dark:border-red-800 p-2">
                <summary className="cursor-pointer text-sm flex items-center gap-2 font-medium text-red-800 dark:text-red-200">
                  <AlertTriangle className="w-4 h-4" /> {result.errors.length} con errores · ver detalle
                </summary>
                <ul className="mt-2 list-disc pl-5 text-xs space-y-0.5">
                  {result.errors.map((e, i) => (
                    <li key={i}>Fila {e.row_number}: <b>{e.name}</b> — {e.reason}</li>
                  ))}
                </ul>
              </details>
            )}

            <DialogFooter>
              <Button onClick={() => onClose(false)} data-testid="import-close-btn">Cerrar</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default ImportResidentsDialog;
